import React from "react";
import { Col, Row, Button } from "react-bootstrap";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import { CircularProgress } from "@mui/material";
import fileDownload from "js-file-download";
import moment from "moment";

import SearchFilter from "../CustomUIComponents/SearchFilter/SearchFilter";
import AnalyticsChart from "../CustomUIComponents/AnalyticsChart/AnalyticsChart";
import { Strings } from "../../resources/i18n/i18n";
import { OPERATION_STATUS } from "../../config/axios.init";
import Utility from "../../utils/Utility";
import {
  ANALYTICS_CHART_PALETTES,
  DATE_TYPE,
  DEFAULT_DATE_FORMAT,
  SEARCH_FILTER_FIELDS,
  SEARCH_FILTER_TYPE,
} from "../../constants/appConstants";
import {
  downloadAnalyticsReportAction,
  getAnalyticsChartDataAction,
} from "./DatabaseTableAction";
import {
  ANALYTICS_REPORT_EARLIEST_YEAR,
  ANALYTICS_REPORT_MAX_RANGE_DAYS,
  ANALYTICS_REPORT_MAX_DAY_BUCKETS,
  ANALYTICS_REPORT_GRANULARITIES,
  ANALYTICS_CHART_CONFIG,
  ANALYTICS_CHART_GROUPS,
  ANALYTICS_DATE_PRESETS,
  ANALYTICS_KPI_TILES,
  ANALYTICS_KPI_NEUTRAL,
  ANALYTICS_PREFS_STORAGE_KEY,
} from "./Constants";
import ExcelDownload from "../../resources/images/ic_download_excel.svg";
import "./DatabaseTables.scss";
// Matches the server: past a month the workbook is emailed rather than streamed.
const EMAIL_THRESHOLD_DAYS = 31;
// Not a failure any more: the server emails the workbook it finished building after we gave up.
const GATEWAY_TIMEOUT_STATUSES = new Set([408, 504]);

class AnalyticsReport extends React.Component {
  constructor(props) {
    super(props);
    const prefs = AnalyticsReport.loadPrefs();
    this.state = {
      ...AnalyticsReport.presetRange("yearToDate"),
      granularity: "AUTO",
      loading: false,
      downloading: false,
      successMessage: "",
      errorMessage: "",
      data: null,
      palette: prefs.palette || "default",
      chartTypes: prefs.chartTypes || {},
      activeGroup: prefs.activeGroup || ANALYTICS_CHART_GROUPS[0].key,
      openTables: {},
    };
  }

  componentDidMount() {
    this.loadCharts();
  }

  static presetRange(key) {
    const now = moment();
    switch (key) {
      case "thisMonth":
        return {
          fromDate: now.clone().startOf("month").format(DEFAULT_DATE_FORMAT),
          toDate: now.clone().format(DEFAULT_DATE_FORMAT),
        };
      case "lastMonth":
        return {
          fromDate: now
            .clone()
            .subtract(1, "month")
            .startOf("month")
            .format(DEFAULT_DATE_FORMAT),
          toDate: now
            .clone()
            .subtract(1, "month")
            .endOf("month")
            .format(DEFAULT_DATE_FORMAT),
        };
      case "thisQuarter":
        return {
          fromDate: now.clone().startOf("quarter").format(DEFAULT_DATE_FORMAT),
          toDate: now.clone().format(DEFAULT_DATE_FORMAT),
        };
      case "last12Months":
        return {
          fromDate: now
            .clone()
            .subtract(11, "months")
            .startOf("month")
            .format(DEFAULT_DATE_FORMAT),
          toDate: now.clone().format(DEFAULT_DATE_FORMAT),
        };
      case "yearToDate":
      default:
        return {
          fromDate: now.clone().startOf("year").format(DEFAULT_DATE_FORMAT),
          toDate: now.clone().format(DEFAULT_DATE_FORMAT),
        };
    }
  }


  static loadPrefs() {
    try {
      return JSON.parse(localStorage.getItem(ANALYTICS_PREFS_STORAGE_KEY)) || {};
    } catch (error) {
      // Corrupt or absent preferences are not worth failing the screen over.
      return {};
    }
  }

  savePrefs = () => {
    try {
      localStorage.setItem(
        ANALYTICS_PREFS_STORAGE_KEY,
        JSON.stringify({
          palette: this.state.palette,
          chartTypes: this.state.chartTypes,
          activeGroup: this.state.activeGroup,
        })
      );
    } catch (error) {
      // Storage can be full or blocked; the screen still works without remembering.
    }
  };


  validateRange = () => {
    const from = moment(this.state.fromDate);
    const to = moment(this.state.toDate);
    if (!this.state.fromDate || !this.state.toDate || !from.isValid() || !to.isValid()) {
      return "analyticsReportDateRequired";
    }
    if (from.isAfter(to, "day")) {
      return "analyticsReportInvalidRange";
    }
    if (from.year() < ANALYTICS_REPORT_EARLIEST_YEAR) {
      return "analyticsReportDateTooEarly";
    }
    if (to.isAfter(moment(), "day")) {
      return "analyticsReportDateInFuture";
    }
    const days = to.diff(from, "days") + 1;
    if (days > ANALYTICS_REPORT_MAX_RANGE_DAYS) {
      return "analyticsReportSpanTooWide";
    }
    if (this.state.granularity === "DAY" && days > ANALYTICS_REPORT_MAX_DAY_BUCKETS) {
      return "analyticsReportDayRangeTooWide";
    }
    return null;
  };

  rangeDays = () =>
    moment(this.state.toDate).diff(moment(this.state.fromDate), "days") + 1;

  handleDateChange = (key) => (dateMoment) => {
    let value;
    if (key === DATE_TYPE.FROM_DATE) {
      value = dateMoment.isAfter(this.state.toDate)
        ? this.state.fromDate
        : dateMoment.clone().startOf("day").format(DEFAULT_DATE_FORMAT);
    } else {
      value = dateMoment.isBefore(this.state.fromDate)
        ? this.state.toDate
        : dateMoment.clone().endOf("day").format(DEFAULT_DATE_FORMAT);
    }
    this.setState({ [key]: value, successMessage: "", errorMessage: "" });
  };

  handlePresetClick = (key) => () => {
    this.setState(
      { ...AnalyticsReport.presetRange(key), successMessage: "", errorMessage: "" },
      this.loadCharts
    );
  };

  // A filter, not an action: it takes effect on Apply, the same as the date range. Reloading on
  // every dropdown change fired a full scan the user had not asked for yet.
  handleGranularityChange = (event) => {
    this.setState({ granularity: event.target.value, successMessage: "", errorMessage: "" });
  };

  handlePaletteChange = (event) => {
    this.setState({ palette: event.target.value }, this.savePrefs);
  };

  handleChartTypeChange = (chartId, type) => {
    this.setState(
      (previous) => ({ chartTypes: { ...previous.chartTypes, [chartId]: type } }),
      this.savePrefs
    );
  };

  handleGroupChange = (index) => {
    this.setState(
      { activeGroup: ANALYTICS_CHART_GROUPS[index].key },
      this.savePrefs
    );
  };

  handleToggleTable = (chartId) => () => {
    this.setState((previous) => ({
      openTables: { ...previous.openTables, [chartId]: !previous.openTables[chartId] },
    }));
  };

  handleReset = () => {
    this.setState(
      {
        ...AnalyticsReport.presetRange("yearToDate"),
        granularity: "AUTO",
        successMessage: "",
        errorMessage: "",
        chartTypes: {},
        openTables: {},
      },
      this.loadCharts
    );
  };

  loadCharts = () => {
    // One at a time. Each load is a heavy scan of the same tables, so firing a second while the
    // first is running makes both slower rather than getting an answer sooner.
    if (this.state.loading) {
      return;
    }
    const invalid = this.validateRange();
    if (invalid) {
      this.setState({ errorMessage: Strings(invalid), successMessage: "" });
      return;
    }
    const fromMillis = Utility.convertDateIntoMiliSeconds(this.state.fromDate);
    const toMillis = Utility.convertDateIntoMiliSeconds(this.state.toDate);
    const requestId = (this.lastRequestId || 0) + 1;
    this.lastRequestId = requestId;
    this.setState({ loading: true, errorMessage: "", successMessage: "" });
    getAnalyticsChartDataAction(
      fromMillis,
      toMillis,
      this.state.granularity,
      (response) => {
        // A superseded response must not overwrite a newer one.
        if (requestId !== this.lastRequestId) {
          return;
        }
        if (response.status === OPERATION_STATUS.SUCCESS && response.data?.data) {
          this.setState({ loading: false, data: response.data.data });
        } else {
          this.setState({
            loading: false,
            errorMessage:
              response.data?.msg ??
              response.error?.message ??
              Strings("analyticsReportFailed"),
          });
        }
      }
    );
  };

  handleDownload = () => {
    const invalid = this.validateRange();
    if (invalid) {
      this.setState({ errorMessage: Strings(invalid), successMessage: "" });
      return;
    }
    const { fromDate, toDate, granularity } = this.state;
    this.setState({ downloading: true, successMessage: "", errorMessage: "" });

    downloadAnalyticsReportAction(
      Utility.convertDateIntoMiliSeconds(fromDate),
      Utility.convertDateIntoMiliSeconds(toDate),
      granularity,
      (response) => {
        if (response.status !== OPERATION_STATUS.SUCCESS) {
          const timedOut = GATEWAY_TIMEOUT_STATUSES.has(
            response.error?.code
          );
          this.setState(
            timedOut
              ? {
                  downloading: false,
                  successMessage: Strings("analyticsReportEmailFallback"),
                }
              : {
                  downloading: false,
                  errorMessage:
                    response.error?.message ??
                    Strings("analyticsReportFailed"),
                }
          );
          return;
        }
        if (Utility.checkBlobTypeForFileDownload(response.data.type)) {
          const range =
            moment(fromDate).format("YYYYMMDD") +
            "-" +
            moment(toDate).format("YYYYMMDD");
          fileDownload(
            response.data,
            Strings("analyticsReportFileName") + "-" + range + ".xlsx"
          );
          this.setState({
            downloading: false,
            successMessage: Strings("analyticsReportDownloaded"),
          });
        } else {
          this.readBlobError(response.data);
        }
      }
    );
  };


  readBlobError = (blob) => {
    blob
      .text()
      .then((text) => {
        let message = Strings("analyticsReportFailed");
        let emailed = false;
        try {
          const parsed = JSON.parse(text);
          // A wide range is handed to email instead of streamed, and answers with the envelope.
          if (parsed.status === 200) {
            emailed = true;
          } else if (parsed.msg) {
            message = parsed.msg;
          }
        } catch (error) {
          // Not the JSON envelope; keep the generic message.
        }
        this.setState(
          emailed
            ? { downloading: false, successMessage: Strings("analyticsReportEmailQueued") }
            : { downloading: false, errorMessage: message }
        );
      })
      .catch(() => {
        this.setState({
          downloading: false,
          errorMessage: Strings("analyticsReportFailed"),
        });
      });
  };

  aggregateBy = (rows, keyField, valueFields) => {
    const totals = new Map();
    (rows || []).forEach((row) => {
      const key = row[keyField] ?? Strings("analyticsChartOther");
      const current = totals.get(key) || { [keyField]: key };
      valueFields.forEach((field) => {
        current[field] = (current[field] || 0) + Number(row[field] || 0);
      });
      totals.set(key, current);
    });
    return [...totals.values()].sort(
      (a, b) => Number(b[valueFields[0]] || 0) - Number(a[valueFields[0]] || 0)
    );
  };

  labelFrequencyTerm = (rows) =>
    (rows || []).map((row) => ({
      ...row,
      frequencyTerm: String(row.frequency ?? "-") + " / " + String(row.term ?? "-"),
    }));

  rowsForChart = (config) => {
    const data = this.state.data;
    if (!data) {
      return [];
    }
    const raw = data[config.dataset] || [];
    if (config.id === "freqTerm") {
      return this.labelFrequencyTerm(raw);
    }
    if (config.aggregateBy) {
      return this.aggregateBy(
        raw,
        config.aggregateBy,
        config.series.map((one) => one.key)
      );
    }
    return raw;
  };

  isEmptyRange = () => {
    const data = this.state.data;
    if (!data) {
      return false;
    }
    const sum = (rows, key) =>
      (rows || []).reduce((total, row) => total + Number(row[key] || 0), 0);
    return (
      sum(data.disbursed, "loans_disbursed") === 0 &&
      sum(data.collections, "total_collected") === 0 &&
      sum(data.applications, "applications_created") === 0
    );
  };

  renderMessages = () => (
    <>
      {this.state.successMessage && (
        <Row className="mb-2">
          <Col>
            <div className="successMessage">{this.state.successMessage}</div>
          </Col>
        </Row>
      )}
      {this.state.errorMessage && (
        <Row className="mb-2">
          <Col>
            <div className="errors p-2 errorMessage">{this.state.errorMessage}</div>
          </Col>
        </Row>
      )}
    </>
  );

  renderPresets = () => (
    <div className="analyticsPresets">
      {ANALYTICS_DATE_PRESETS.map((preset) => {
        const range = AnalyticsReport.presetRange(preset.key);
        const active =
          moment(range.fromDate).isSame(this.state.fromDate, "day") &&
          moment(range.toDate).isSame(this.state.toDate, "day");
        return (
          <button
            type="button"
            key={preset.key}
            aria-pressed={active}
            disabled={this.state.loading}
            className={active ? "analyticsPreset active" : "analyticsPreset"}
            onClick={this.handlePresetClick(preset.key)}
          >
            {Strings(preset.label)}
          </button>
        );
      })}
    </div>
  );

  renderKpiRow = () => {
    const comparison = this.state.data?.comparison || [];
    const byKey = new Map(comparison.map((row) => [row.measure_key, row]));
    const portfolio = this.state.data?.portfolio || [];
    const activeLoans = portfolio.length ? portfolio[0].active_loans : null;

    return (
      <Row className="analyticsKpiRow">
        {ANALYTICS_KPI_TILES.map((tile) => {
          const row = byKey.get(tile.key);
          const value = row ? Number(row.current_period || 0) : 0;
          const change = row ? row.change_percent : null;
          const up = change !== null && change !== undefined && Number(change) >= 0;
          return (
            <Col key={tile.key} className="analyticsKpiCol">
              <div
                className="analyticsKpiTile"
                style={{ background: tile.tint, borderTopColor: tile.accent }}
              >
                {this.renderSparkline(tile)}
                <div className="analyticsKpiLabel">{Strings(tile.label)}</div>
                <div className="analyticsKpiValue">
                  {tile.money
                    ? value.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                    : value.toLocaleString()}
                </div>
                {change === null || change === undefined ? (
                  <div className="analyticsKpiDeltaMuted">
                    {Strings("kpi_noBaseline")}
                  </div>
                ) : (
                  <div className={up ? "analyticsKpiDeltaUp" : "analyticsKpiDeltaDown"}>
                    {/* Arrow, not colour, is what carries direction for a colourblind or
                        greyscale reader. */}
                    {(up ? "↗ " : "↘ ") + Math.abs(Number(change)) + "%"}
                    <span className="analyticsKpiDeltaNote">
                      {" " + Strings("kpi_vsPrevious")}
                    </span>
                  </div>
                )}
              </div>
            </Col>
          );
        })}
        <Col className="analyticsKpiCol">
          <div
            className="analyticsKpiTile"
            style={{
              background: ANALYTICS_KPI_NEUTRAL.tint,
              borderTopColor: ANALYTICS_KPI_NEUTRAL.accent,
            }}
          >
            {this.renderSparkline(ANALYTICS_KPI_NEUTRAL)}
            <div className="analyticsKpiLabel">{Strings("kpi_activeLoans")}</div>
            <div className="analyticsKpiValue">
              {activeLoans === null ? "-" : Number(activeLoans).toLocaleString()}
            </div>
            <div className="analyticsKpiDeltaMuted">{Strings("kpi_today")}</div>
          </div>
        </Col>
      </Row>
    );
  };

  /**
   * The tile's own history, drawn faintly behind the number.
   *
   * A stat tile is the right form for a single headline figure, but on its own it says nothing
   * about the shape of the period; the sparkline restores that without competing with the
   * value. Deliberately unlabelled and low contrast - it is a hint, and the number stays the
   * subject. Returns nothing when there is too little to plot.
   */
  renderSparkline = (tile) => {
    const data = this.state.data;
    if (!data || !tile.dataset || !tile.seriesKey) {
      return null;
    }
    const rows = data[tile.dataset] || [];
    let values;
    if (tile.aggregatePeriods) {
      // One row per period per category, so collapse to a single figure per period.
      const byPeriod = new Map();
      rows.forEach((row) => {
        const key = String(row.month);
        byPeriod.set(key, (byPeriod.get(key) || 0) + Number(row[tile.seriesKey] || 0));
      });
      values = [...byPeriod.entries()].sort((a, b) => (a[0] < b[0] ? -1 : 1)).map((e) => e[1]);
    } else {
      values = rows.map((row) => Number(row[tile.seriesKey] || 0));
    }
    if (values.length < 2) {
      return null;
    }

    const W = 100;
    const H = 30;
    const max = Math.max(...values);
    const min = Math.min(...values);
    const span = max - min || 1;
    const step = W / (values.length - 1);
    const points = values.map((v, i) => [
      i * step,
      // 2px of headroom top and bottom so peaks are not clipped by the viewBox
      H - 2 - ((v - min) / span) * (H - 4),
    ]);
    const line = points.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
    const area = `${line} ${W},${H} 0,${H}`;

    return (
      <svg
        className="analyticsKpiSpark"
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        aria-hidden="true"
        focusable="false"
      >
        <polygon points={area} fill={tile.accent} fillOpacity="0.13" />
        <polyline
          points={line}
          fill="none"
          stroke={tile.accent}
          strokeOpacity="0.55"
          strokeWidth="1.6"
          vectorEffect="non-scaling-stroke"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      </svg>
    );
  };

  renderControls = () => {
    const { granularity, palette, loading, downloading } = this.state;
    const busy = loading || downloading;

    // Everything lives in ONE SearchFilter grid so widths and heights come from the same
    // component. Two separate rows could not be made to line up: the date inputs are
    // TextFieldComponents inside a md=5 column, while hand-placed selects were md=2 carrying
    // their own padding, so the two rows never landed on the same gutters.
    const filters = [
      {
        type: SEARCH_FILTER_TYPE.DATE,
        fromDate: this.state.fromDate,
        toDate: this.state.toDate,
        handleCalenderChange: this.handleDateChange,
        fieldname: SEARCH_FILTER_FIELDS.DATE,
        column: 5,
        paddingTop: true,
      },
      {
        type: SEARCH_FILTER_TYPE.SELECTION,
        id: "analyticsGranularity",
        name: "granularity",
        fieldname: "granularity",
        column: 2,
        paddingTop: true,
        Value: granularity,
        handleOnChange: this.handleGranularityChange,
        filterDropdownList: ANALYTICS_REPORT_GRANULARITIES.map((option) => ({
          value: option,
          label:
            Strings("analyticsChartGranularity") + ": " + Strings("granularity_" + option),
        })),
      },
      {
        type: SEARCH_FILTER_TYPE.SELECTION,
        id: "analyticsPalette",
        name: "palette",
        fieldname: "palette",
        column: 2,
        paddingTop: true,
        Value: palette,
        handleOnChange: this.handlePaletteChange,
        filterDropdownList: Object.keys(ANALYTICS_CHART_PALETTES).map((name) => ({
          value: name,
          label: Strings("analyticsChartPalette") + ": " + Strings("palette_" + name),
        })),
      },
    ];

    return (
      <>
        <div className="analyticsToolbar">
          {this.renderPresets()}
          <div className="analyticsToolbarActions">
            {this.rangeDays() > EMAIL_THRESHOLD_DAYS && (
              <span className="analyticsHint">
                {Strings("analyticsReportEmailHint")}
              </span>
            )}
            {busy && <CircularProgress size={18} />}
            <Button
              className="downloadButtons"
              onClick={this.handleDownload}
              disabled={busy}
              title={
                this.rangeDays() > EMAIL_THRESHOLD_DAYS
                  ? Strings("analyticsReportEmail")
                  : Strings("analyticsReportDownload")
              }
            >
              <img src={ExcelDownload} alt={Strings("analyticsReportDownload")} />
            </Button>

          </div>
        </div>
        <SearchFilter
          filters={filters}
          handleOnClickSearchFilter={this.loadCharts}
          handleOnClickResetFilter={this.handleReset}
          paddingRight={0}
          paddingTop={2}
          removeLeftPadding={true}
        />
      </>
    );
  };

  renderChartTable = (config, rows) => {
    const columns = [config.xKey, ...config.series.map((one) => one.key)];
    const headers = [
      Strings("analyticsChartCategory"),
      ...config.series.map((one) => Strings(one.label)),
    ];
    return (
      <div className="analyticsChartTableWrap">
        <table className="analyticsChartTable">
          <thead>
            <tr>
              {headers.map((header) => (
                <th key={header}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={String(row[config.xKey]) + index}>
                {columns.map((column, columnIndex) => (
                  <td key={column} className={columnIndex ? "numeric" : ""}>
                    {row[column] === null || row[column] === undefined
                      ? ""
                      : typeof row[column] === "number"
                      ? row[column].toLocaleString()
                      : String(row[column])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  renderChartCard = (config) => {
    const rows = this.rowsForChart(config);
    const type = this.state.chartTypes[config.id] || config.defaultType;
    const tableOpen = !!this.state.openTables[config.id];
    return (
      <Col md={config.width || 6} className="pt-3" key={config.id}>
        <div className="analyticsChartCard">
          <AnalyticsChart
            actions={
              <>
                {config.types &&
                  config.types.length > 1 &&
                  config.types.map((option) => (
                    <button
                      type="button"
                      key={option}
                      aria-pressed={option === type}
                      className={
                        option === type
                          ? "analyticsChartTypeButton active"
                          : "analyticsChartTypeButton"
                      }
                      onClick={() => this.handleChartTypeChange(config.id, option)}
                    >
                      {Strings("chartType_" + option)}
                    </button>
                  ))}
                <button
                  type="button"
                  aria-pressed={tableOpen}
                  className={
                    tableOpen
                      ? "analyticsChartTypeButton active"
                      : "analyticsChartTypeButton"
                  }
                  onClick={this.handleToggleTable(config.id)}
                >
                  {Strings("analyticsChartShowNumbers")}
                </button>
              </>
            }
            type={type}
            data={rows}
            series={config.series.map((one) => ({
              key: one.key,
              label: Strings(one.label),
            }))}
            xKey={config.xKey}
            palette={this.state.palette}
            height={config.height || 300}
            money={config.money}
            maxSlices={config.maxSlices}
            ordinal={config.ordinal}
            title={Strings(config.title)}
            subtitle={config.subtitle ? Strings(config.subtitle) : undefined}
          />
          {tableOpen && this.renderChartTable(config, rows)}
        </div>
      </Col>
    );
  };

  renderChartTabs = () => {
    const activeIndex = Math.max(
      0,
      ANALYTICS_CHART_GROUPS.findIndex((group) => group.key === this.state.activeGroup)
    );
    return (
      <Tabs selectedIndex={activeIndex} onSelect={this.handleGroupChange}>
        <TabList>
          {ANALYTICS_CHART_GROUPS.map((group) => (
            <Tab key={group.key} className="react-tabs__tab mt-1">
              {Strings(group.label)}
            </Tab>
          ))}
        </TabList>
        {ANALYTICS_CHART_GROUPS.map((group) => (
          <TabPanel key={group.key}>
            <Row>
              {ANALYTICS_CHART_CONFIG.filter(
                (config) => config.group === group.key
              ).map(this.renderChartCard)}
            </Row>
          </TabPanel>
        ))}
      </Tabs>
    );
  };

  render() {
    const { data, loading } = this.state;
    return (
      <div className="duplicateEntryManagement">
        <Row className="paper">
          <Col md={12} className="ps-0 pe-0 pb-2 pt-2">
            <hr className="newhr opacity-100" />
          </Col>

          <Col md={12}>{this.renderControls()}</Col>

          <Col md={12} className="pt-3">{this.renderMessages()}</Col>

          {data && this.isEmptyRange() && (
            <Col md={12}>
              <div className="analyticsEmptyState">

                <div>{Strings("analyticsReportEmptyRange")}</div>
              </div>
            </Col>
          )}

          {data && !this.isEmptyRange() && (
            <Col
              md={12}
              className={loading ? "analyticsRefreshing" : undefined}
              aria-busy={loading}
            >
              {this.renderKpiRow()}
              {this.renderChartTabs()}
            </Col>
          )}
        </Row>
      </div>
    );
  }
}

export default AnalyticsReport;
